import { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import { HiExternalLink } from "react-icons/hi";
import useNameplateScanHook from "@/custolink-views/asset_frontend/scan/useNameplateScanHook";
import takePhoto from "@/custolink-views/asset_frontend/scan/TakePhoto";
import { ImageLike } from "tesseract.js";
import useQrScanHook from "@/custolink-views/asset_frontend/scan/useQrCodeScanHook";
import { useTranslation } from "react-i18next";
import Drawer from "@/components/ui/Drawer/Drawer";
import RdaDrawer from "@/custolink-views/asset_frontend/redesigns/RdaDrawer";
import useUiContext from "@/custolink-hooks/useUiContext";
import AssetDrawerScope from "@/custolink-views/asset_frontend/redesigns/AssetDrawerScope";
import { normaliseHex } from "@/custolink-views/asset_frontend/redesigns/theme";
import { useAssetFrontendDesign } from "@/custolink-views/asset_frontend/redesigns/assetFrontendDesign";
import { PrimaryButton } from "@/custolink-views/asset_frontend/redesigns/formKit";

/*
 * To make Camera work on Chrome (such that navigator.mediaDevices is not undefined)
 * go to chrome://flags/ and add http://asset.custolinkdev.com:9095,http://custolinkdev.com:9095
 *  as  Insecure origins treated as secure (activate as well).
 *  Restart Chrome.
 *
 */
export default function Scan(p: { open: boolean; close: () => void }) {
  const uic = useUiContext();
  const { t } = useTranslation();
  const accent = normaliseHex(
    uic.organisationContext.lookAndFeel.assetFrontendBgcolorHex,
  );
  // Shared with the classic AssetFrontendHome / AssetViewClassic: the drawer
  // frame follows the asset-frontend switch so classic visitors still get the
  // old Drawer chrome, and new-design visitors get RdaDrawer.
  const newDesign = useAssetFrontendDesign();
  const DrawerFrame = newDesign ? RdaDrawer : Drawer;

  const [cameraFrameMode, setCameraFrameMode] = useState<"qr" | "nameplate">(
    "nameplate",
  );
  const hNameplate = useNameplateScanHook();
  const hQr = useQrScanHook();
  const [processing, setProcessing] = useState(false);
  const [qrDebugData] = useState<string | undefined>(undefined);

  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const [canvasEl, setCanvasEl] = useState<HTMLCanvasElement | null>(null);

  const setVideoRef = useCallback((node: HTMLVideoElement | null) => {
    setVideoEl(node);
  }, []);

  const setCanvasRef = useCallback((node: HTMLCanvasElement | null) => {
    setCanvasEl(node);
  }, []);

  const photoboothInstance = useRef<PhotoBooth | null>(null);

  useEffect(() => {
    // Stop when closing or when nodes disappear (unmount)
    if (!p.open || !videoEl || !canvasEl) {
      photoboothInstance.current?.stop();
      photoboothInstance.current = null;
      return;
    }

    // Start once when open + nodes available
    if (!photoboothInstance.current) {
      const pb = new PhotoBooth(videoEl, canvasEl);
      photoboothInstance.current = pb;

      return () => {
        pb.stop();
        photoboothInstance.current = null;
      };
    }
  }, [p.open, videoEl, canvasEl]);

  async function doCapture() {
    const [photoImageLike, imageDataForQr] = takePhoto(
      cameraFrameMode,
      videoEl as HTMLVideoElement,
    );
    if (cameraFrameMode == "qr") {
      await hQr.captureAndRedirect(
        imageDataForQr.data,
        imageDataForQr.width,
        imageDataForQr.height,
      );
    } else {
      await hNameplate.capture(photoImageLike as ImageLike); //photoImageLike as ImageLike);
    }
  }

  return (
    <DrawerFrame
      isOpen={p.open}
      placement="right"
      title={t("scan.title")}
      width={"100%"}
      onClose={p.close}
      onRequestClose={p.close}
    >
      <AssetDrawerScope accent={accent}>
        <div className="flex w-full flex-col">
          <div className="mb-3 grid grid-cols-2 gap-px bg-[var(--rda-ground)]">
            {(["qr", "nameplate"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                className={
                  "rda-tile px-4 py-2.5 text-sm font-semibold " +
                  (cameraFrameMode === mode
                    ? "bg-[var(--rda-accent)] text-[var(--rda-on-accent)]"
                    : "bg-[var(--rda-surface)] text-[var(--rda-on-surface)]")
                }
                onClick={() => setCameraFrameMode(mode)}
              >
                {t(mode === "qr" ? "scan.qr" : "scan.nameplate")}
              </button>
            ))}
          </div>

          <div className="relative w-full" style={{ aspectRatio: "4 / 3" }}>
            <video
              ref={setVideoRef}
              playsInline
              autoPlay
              muted
              className="h-full w-full object-cover"
              style={{ width: "100%", height: "100%" }}
            ></video>
            <div
              className="absolute border-4 border-red-500 rounded-md"
              style={
                cameraFrameMode == "qr" ? qrFrameStyle : nameplateFrameStyle
              }
            />
          </div>

          <div className="flex justify-center pt-4">
            <PrimaryButton
              className="max-w-xs"
              disabled={processing}
              type="button"
              onClick={async () => {
                try {
                  setProcessing(true);
                  await doCapture();
                } finally {
                  setProcessing(false);
                }
              }}
            >
              {t("scan.action.title")}
            </PrimaryButton>
          </div>

          {qrDebugData != undefined && (
            <p className="mt-4 text-center text-sm text-[var(--rda-muted)]">
              {qrDebugData}
            </p>
          )}

          {hNameplate.noResult === true && (
            <div className="mt-4 space-y-1 text-center">
              <h4 className="text-base font-semibold">
                {t("scan.result.notFound")}
              </h4>
              <p className="text-sm text-[var(--rda-muted)]">
                {t("scan.result.notFound.tryAgain")}.
              </p>
            </div>
          )}

          {hNameplate.result != null && (
            <div className="mt-4 space-y-2 text-center">
              <h4 className="text-base font-semibold">
                {t("scan.result.serialFound")}
              </h4>
              <span className="inline-block bg-[var(--rda-neutral)] px-3 py-1 font-mono text-sm text-[var(--rda-on-neutral)]">
                {hNameplate.result}
              </span>
              <a
                className="flex items-center justify-center gap-2 text-sm font-semibold text-[var(--rda-accent-text)] underline"
                href={"/asset/" + hNameplate.result}
                rel="noopener noreferrer"
              >
                <HiExternalLink /> {t("scan.result.link.goToAsset")}
              </a>
            </div>
          )}

          <canvas ref={setCanvasRef} />
        </div>
      </AssetDrawerScope>
    </DrawerFrame>
  );
}

enum CameraMode {
  User = "user",
  Environment = "environment",
}

export abstract class Defaults {
  static width: number = 640;
}

class PhotoBooth {
  mode: CameraMode = CameraMode.Environment;
  canvas: HTMLCanvasElement;
  video: HTMLVideoElement;

  constructor(videoEl: HTMLVideoElement, canvasEl: HTMLCanvasElement) {
    this.video = videoEl;
    this.canvas = canvasEl;
    this.init();
  }

  private init() {
    navigator.mediaDevices
      .enumerateDevices()
      .then(this.on_enumerate_devices.bind(this))
      .catch(this.on_error);
  }

  stop() {
    if (this.video && this.video.srcObject) {
      const stream = this.video.srcObject as MediaStream;

      // Stop all tracks
      stream.getTracks().forEach((track) => track.stop());

      // Remove srcObject and reset video element
      this.video.srcObject = null;

      // Remove event listeners just in case
      this.video.onloadedmetadata = null;
      this.video.oncanplay = null;
    }

    // Optional: remove canvas reference
    this.canvas = null!;
    this.video = null!;
  }

  on_enumerate_devices(): Promise<void> {
    return this.init_camera();
  }

  async init_camera(): Promise<void> {
    try {
      const stream = await this.get_media();
      return this.on_get_media(stream);
    } catch (reason) {
      return this.on_error(reason);
    }
  }

  get_media(): Promise<MediaStream> {
    const constraints = {
      audio: false,
      video: { facingMode: this.mode },
    };
    return navigator.mediaDevices.getUserMedia(constraints);
  }

  on_get_media = (stream: MediaStream) => {
    this.video.onloadedmetadata = () => {
      this.video.play();
    };
    this.video.oncanplay = () => {
      this.on_video_ready();
    };
    this.video.srcObject = stream;
  };

  on_video_ready = () => {
    this.canvas.width = Defaults.width;
    this.canvas.height =
      (this.video.videoHeight / (this.video.videoWidth / Defaults.width)) * 0.8;

//    this.video.setAttribute("height", this.canvas.height.toString());
//    this.video.setAttribute("width", this.canvas.width.toString());
  };

  on_error = (reason: unknown) => {
    console.error(reason);
  };

  switch_cam() {
    this.mode =
      this.mode == CameraMode.User ? CameraMode.Environment : CameraMode.User;
    this.init_camera();
  }
}

export const nameplateFrameStyleTop = 0.1;
export const nameplateFrameStyleLeft = 0.1;
export const nameplateFrameStyleWidth = 0.8;
export const nameplateFrameStyleHeight = 0.8;
const nameplateFrameStyle: CSSProperties = {
  top: nameplateFrameStyleTop * 100 + "%", // relative to video/container
  left: nameplateFrameStyleLeft * 100 + "%",
  width: nameplateFrameStyleWidth * 100 + "%",
  height: nameplateFrameStyleHeight * 100 + "%",
  pointerEvents: "none",
  boxSizing: "border-box",
  borderStyle: "dashed",
  borderWidth: "4px",
  borderColor: "red",
};

export const qrFrameStyleTop = 0.1;
export const qrFrameStyleLeft = 0.1;
export const qrFrameStyleWidth = 0.8;
export const qrFrameStyleHeight = 0.8;
const qrFrameStyle: CSSProperties = {
  top: qrFrameStyleTop * 100 + "%", // relative to video/container
  left: qrFrameStyleLeft * 100 + "%",
  width: qrFrameStyleWidth * 100 + "%",
  height: qrFrameStyleHeight * 100 + "%",
  pointerEvents: "none",
  boxSizing: "border-box",
  borderStyle: "dashed",
  borderWidth: "4px",
  borderColor: "red",
};
